import React, { useMemo } from 'react';
import Jazzicon, { jsNumberForAddress } from 'react-jazzicon';
import { copyToClipboard, shortenWalletAddress } from '../../../utils/commonFunction';
import { formatDateTime } from '../formatting';
import { computeEmployeeWorkspaceMetrics } from '../utils/employeeTaskMetrics';
import {
    STATS_WEEK_COUNT,
    buildActivityCalendar,
    computeStatsSummary,
} from '../utils/workStatsMetrics';
import TaskCardCompact from './TaskCardCompact';
import TaskSection from './TaskSection';
import WorkActivityCalendar from './WorkActivityCalendar';

const StatCard = ({ label, value, hint, accent = 'default' }) => {
    const valueClass =
        accent === 'teal'
            ? 'text-teal-700 dark:text-teal-400'
            : accent === 'amber'
              ? 'text-amber-700 dark:text-amber-400'
              : 'text-zinc-900 dark:text-white';

    return (
        <div className="rounded-xl border border-zinc-200 bg-zinc-50/80 p-4 dark:border-zinc-800 dark:bg-zinc-900/40">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{label}</p>
            <p className={`mt-2 text-2xl font-bold tabular-nums ${valueClass}`}>{value}</p>
            {hint ? <p className="mt-1 text-xs text-zinc-500">{hint}</p> : null}
        </div>
    );
};

const StatusPill = ({ status }) => {
    const normalized = String(status || 'active').toLowerCase();
    const isActive = normalized === 'active';
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${
                isActive
                    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                    : 'border-zinc-300 bg-zinc-100 text-zinc-600 dark:border-zinc-600 dark:bg-zinc-800/60 dark:text-zinc-400'
            }`}
        >
            <span
                className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-zinc-400'}`}
            />
            {isActive ? 'Active' : normalized === 'removed' ? 'Removed' : 'Inactive'}
        </span>
    );
};

const EmployeeDetailPage = ({
    employee,
    tokenSymbol = 'USDC',
    formatNumber,
    onOpenTask,
    onBack,
    isCompanyOwner = false,
    onEditEmployee,
    onToggleStatus,
    statusUpdating = false,
}) => {
    const employeeTasks = employee?.tasks || [];

    const metrics = useMemo(
        () => computeEmployeeWorkspaceMetrics(employeeTasks),
        [employeeTasks],
    );

    const activityCalendar = useMemo(
        () => buildActivityCalendar(employeeTasks, 'employee'),
        [employeeTasks],
    );

    const activitySummary = useMemo(
        () => computeStatsSummary(employeeTasks, 'employee'),
        [employeeTasks],
    );

    const formatAmt = (n) => `${formatNumber(n)} ${tokenSymbol}`;

    const hourlyLabel =
        employee?.hourlyRate != null && employee.hourlyRate !== ''
            ? `$${Number(employee.hourlyRate).toFixed(2)} ${tokenSymbol}/hr`
            : '—';

    const isActive = String(employee?.status || 'active').toLowerCase() === 'active';

    const renderEmployeeTaskRow = (task) => (
        <TaskCardCompact
            task={task}
            tokenSymbol={tokenSymbol}
            onOpenDetail={onOpenTask}
        />
    );

    return (
        <div className="mx-auto w-full max-w-6xl space-y-6 pb-10">
            <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-zinc-500 transition hover:text-teal-700 dark:text-zinc-400 dark:hover:text-teal-400"
            >
                <i className="fa-solid fa-arrow-left text-[10px]" />
                Back to employees
            </button>

            <div className="flex flex-col gap-4 rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex min-w-0 items-start gap-4">
                    {employee?.wallet ? (
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-zinc-100 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-zinc-700">
                            <Jazzicon
                                diameter={56}
                                seed={jsNumberForAddress(employee.wallet)}
                            />
                        </div>
                    ) : null}
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <h1 className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-white">
                                {employee?.name || 'Unnamed employee'}
                            </h1>
                            <StatusPill status={employee?.status} />
                        </div>
                        {employee?.wallet ? (
                            <div className="mt-2 inline-flex max-w-full flex-wrap items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 dark:border-zinc-700 dark:bg-zinc-900/60">
                                <span className="font-mono text-xs text-teal-700 dark:text-teal-400">
                                    {shortenWalletAddress(employee.wallet)}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => copyToClipboard(employee.wallet)}
                                    className="text-zinc-400 hover:text-teal-600 dark:hover:text-teal-400"
                                    title="Copy wallet"
                                >
                                    <i className="fa-regular fa-copy text-xs" />
                                </button>
                            </div>
                        ) : null}
                        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
                            <span className="font-medium text-zinc-500 dark:text-zinc-500">
                                Hourly rate:{' '}
                            </span>
                            <span className="font-semibold text-zinc-800 dark:text-zinc-200">
                                {hourlyLabel}
                            </span>
                        </p>
                        {employee?.createdAt ? (
                            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-500">
                                Joined{' '}
                                <span className="font-medium text-zinc-700 dark:text-zinc-300">
                                    {formatDateTime(employee.createdAt)}
                                </span>
                            </p>
                        ) : null}
                    </div>
                </div>

                {isCompanyOwner ? (
                    <div className="flex flex-wrap gap-2 sm:shrink-0">
                        <button
                            type="button"
                            onClick={() => onEditEmployee?.(employee)}
                            className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 shadow-sm transition hover:border-teal-300 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
                        >
                            Edit employee
                        </button>
                        {onToggleStatus ? (
                            <button
                                type="button"
                                disabled={statusUpdating}
                                onClick={() => onToggleStatus(employee)}
                                className={`rounded-lg border px-3 py-2 text-xs font-semibold shadow-sm transition disabled:opacity-50 ${
                                    isActive
                                        ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200'
                                        : 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200'
                                }`}
                            >
                                {statusUpdating
                                    ? 'Updating…'
                                    : isActive
                                      ? 'Deactivate'
                                      : 'Activate'}
                            </button>
                        ) : null}
                    </div>
                ) : null}
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                    label="Paid by company"
                    value={formatAmt(metrics.totalReleased)}
                    hint={`${metrics.releasedCount} completed payout${metrics.releasedCount === 1 ? '' : 's'}`}
                    accent="teal"
                />
                <StatCard
                    label="Awaiting release"
                    value={formatAmt(metrics.pendingRelease)}
                    hint={`${metrics.pendingCount} submitted`}
                    accent="amber"
                />
                <StatCard
                    label="In escrow"
                    value={formatAmt(metrics.inEscrow)}
                    hint={`${metrics.lockedCount} task${metrics.lockedCount === 1 ? '' : 's'} with funds held`}
                />
                <StatCard
                    label="Tasks"
                    value={metrics.totalTasks}
                    hint={`${metrics.assignedCount} open · ${metrics.completedCount} done`}
                />
            </div>

            <section className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">
                            Work activity
                        </h2>
                        <p className="mt-0.5 text-xs text-zinc-500">
                            GitHub-style calendar · last {STATS_WEEK_COUNT} weeks
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-4 text-xs text-zinc-600 dark:text-zinc-400">
                        <span>
                            <span className="font-semibold text-zinc-900 dark:text-white">
                                {activitySummary.activeDays}
                            </span>{' '}
                            active day{activitySummary.activeDays === 1 ? '' : 's'}
                        </span>
                        <span>
                            <span className="font-semibold text-zinc-900 dark:text-white">
                                {activitySummary.submissions}
                            </span>{' '}
                            submission{activitySummary.submissions === 1 ? '' : 's'}
                        </span>
                        <span>
                            <span className="font-semibold text-zinc-900 dark:text-white">
                                {activitySummary.totalEvents}
                            </span>{' '}
                            total event{activitySummary.totalEvents === 1 ? '' : 's'}
                        </span>
                    </div>
                </div>
                <div className="mt-4">
                    <WorkActivityCalendar
                        calendar={activityCalendar}
                        role="employee"
                        hint="Days this employee submitted work or had task activity on assigned tasks."
                    />
                </div>
            </section>

            <TaskSection
                title="Assigned tasks"
                description="Active work for this employee — not yet completed or released."
                items={metrics.assigned}
                emptyMessage="No active assignments for this employee."
                renderTask={renderEmployeeTaskRow}
                showStatusFilter
            />

            <TaskSection
                title="Completed tasks"
                description="Approved or released work — payouts already sent from your company."
                items={metrics.completed}
                emptyMessage="No completed tasks yet for this employee."
                renderTask={renderEmployeeTaskRow}
                showStatusFilter={false}
            />
        </div>
    );
};

export default EmployeeDetailPage;
